import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import AppSidebar from '@/components/Sidebar';
import Header from '@/components/Header';
import { useWebsites } from '@/context/WebsitesContext';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DEFAULT_SECTION_TYPES } from '@/config/sectionDefaults';

interface SectionType {
  id: string;
  name: string;
  description: string | null;
  html_example?: string;
  section_notes?: string | null;
}

interface SectionRule {
  id: string;
  website_id: string;
  section_id: string;
  template_type: string;
  content_type: string;
  enabled: boolean;
  html_example: string;
  section_notes: string | null;
}

export const SectionRules = () => {
  const navigate = useNavigate();
  const { currentWebsite } = useWebsites();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sectionTypes, setSectionTypes] = useState<SectionType[]>([]);
  const [sectionRules, setSectionRules] = useState<SectionRule[]>([]);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!currentWebsite) return;

      try {
        setLoading(true);
        const [sectionTypesData, sectionRulesData] = await Promise.all([
          supabase
            .from('section_types')
            .select('*')
            .order('name'),
          supabase
            .from('website_section_rules')
            .select('*')
            .eq('website_id', currentWebsite.id)
        ]);

        if (sectionTypesData.error) throw sectionTypesData.error;
        if (sectionRulesData.error) throw sectionRulesData.error;

        // Merge database section types with defaults
        const dbSectionTypes = sectionTypesData.data || [];
        const mergedSectionTypes = DEFAULT_SECTION_TYPES.map(defaultType => {
          const dbType = dbSectionTypes.find(t => t.id === defaultType.id);
          return {
            ...defaultType,
            ...dbType,
            html_example: dbType?.html_example || defaultType.html_example,
            section_notes: dbType?.section_notes || defaultType.section_notes
          };
        });

        setSectionTypes(mergedSectionTypes);
        setSectionRules(sectionRulesData.data || []);
      } catch (error) {
        console.error('Error loading section data:', error);
        toast.error('Failed to load section data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentWebsite]);

  const handleSave = async (sectionId: string) => {
    if (!currentWebsite) return;

    try {
      setSaving(true);
      const rule = sectionRules.find(r => r.section_id === sectionId);
      const sectionType = sectionTypes.find(s => s.id === sectionId);
      
      if (!sectionType) return;

      // If no rule exists, create one
      if (!rule) {
        const { error } = await supabase
          .from('website_section_rules')
          .insert({
            website_id: currentWebsite.id,
            section_id: sectionId,
            template_type: 'default',
            content_type: 'post',
            enabled: true,
            html_example: sectionType.html_example || '',
            section_notes: sectionType.section_notes
          });

        if (error) throw error;
      } else {
        // Update existing rule
        const { error } = await supabase
          .from('website_section_rules')
          .update({
            html_example: rule.html_example,
            section_notes: rule.section_notes
          })
          .eq('id', rule.id);

        if (error) throw error;
      }

      toast.success('Section rules saved successfully');
    } catch (error) {
      console.error('Error saving section rules:', error);
      toast.error('Failed to save section rules');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRule = (sectionId: string, field: keyof SectionRule, value: string) => {
    setSectionRules(prev => {
      const existingRule = prev.find(r => r.section_id === sectionId);
      if (existingRule) {
        return prev.map(rule => 
          rule.section_id === sectionId
            ? { ...rule, [field]: value }
            : rule
        );
      } else {
        // Create new rule if it doesn't exist
        return [...prev, {
          id: '', // Will be set by database
          website_id: currentWebsite?.id || '',
          section_id: sectionId,
          template_type: 'default',
          content_type: 'post',
          enabled: true,
          html_example: field === 'html_example' ? value : '',
          section_notes: field === 'section_notes' ? value : null
        }];
      }
    });
  };

  if (!currentWebsite) {
    return (
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-auto p-6">
            <div className="max-w-6xl mx-auto">
              <Card>
                <CardContent className="p-6">
                  <p className="text-center text-muted-foreground">Please select a website to configure section rules.</p>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-auto p-6">
            <div className="max-w-6xl mx-auto flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex w-full bg-background">
      <AppSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/template-rules')}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Template Rules
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Section Types</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {sectionTypes.map((section) => (
                      <Button
                        key={section.id}
                        variant={selectedSection === section.id ? "default" : "outline"}
                        className="w-full justify-start"
                        onClick={() => setSelectedSection(section.id)}
                      >
                        {section.name}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {selectedSection && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {sectionTypes.find(s => s.id === selectedSection)?.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="html-example">Content Example</Label>
                        <Textarea
                          id="html-example"
                          value={sectionRules.find(r => r.section_id === selectedSection)?.html_example || 
                                 sectionTypes.find(s => s.id === selectedSection)?.html_example || ''}
                          onChange={(e) => handleUpdateRule(selectedSection, 'html_example', e.target.value)}
                          placeholder="Enter example content for this section..."
                          className="min-h-[400px] font-mono"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="section-notes">Section Notes</Label>
                        <Textarea
                          id="section-notes"
                          value={sectionRules.find(r => r.section_id === selectedSection)?.section_notes || 
                                 sectionTypes.find(s => s.id === selectedSection)?.section_notes || ''}
                          onChange={(e) => handleUpdateRule(selectedSection, 'section_notes', e.target.value)}
                          placeholder="Enter notes or guidelines for this section..."
                          className="min-h-[100px]"
                        />
                      </div>

                      <Button
                        onClick={() => handleSave(selectedSection)}
                        disabled={saving}
                        className="w-full"
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}; 